import { defineStore } from 'pinia';
import IProduct from '@/interfaces/IProduct';
import IImage from '@/interfaces/IImage';
import IPrice from '@/interfaces/IPrice';
import ISpecification from '@/interfaces/ISpecification';
import IDescription from '@/interfaces/IDescription';
import { deduplicateImages } from '@/helpers/images/deduplicateImages';

export const useProductsStore = defineStore('products', {
  state: () => ({
    products: new Array<IProduct>(),
  }),
  getters: {
    getNumberOfProducts(): number {
      return this.products.length;
    },
    getAllImages(): IImage[] {
      return this.products.flatMap((product) => [
        ...product.images,
        ...product.description.images,
      ]);
    },
    getNumberOfImages(): number {
      return this.getAllImages.length;
    },
    // New getter to count images by type
    getImageTypeCounts(): Record<string, number> {
      return this.getAllImages.reduce((acc: Record<string, number>, image: IImage) => {
        acc[image.type] = (acc[image.type] || 0) + 1;
        return acc;
      }, {});
    },
    missingMetaTitleCount(): number {
      return this.products.filter(product => !product.metaTitle).length;
    },
    missingMetaDescriptionCount(): number {
      return this.products.filter(product => !product.metaDescription).length;
    },
    missingDescriptionCount(): number {
      return this.products.filter(product => !product.description.descriptionHtml).length;
    },
    missingNameCount(): number {
      return this.products.filter(product => !product.name).length;
    },
    missingImagesCount(): number {
      return this.products.filter(product => !product.images || product.images.length === 0).length;
    },
    missingPriceCount(): number {
      // Adjust the property check for IPrice if needed.
      return this.products.filter(product => !product.price || !product.price.price).length;
    },
    missingSpecificationCount(): number {
      return this.products.filter(
        product => !product.specification || Object.keys(product.specification).length === 0
      ).length;
    },
    missingMainCategoryCount(): number {
      return this.products.filter(product => !product.mainCategory).length;
    },
  },
  actions: {
    addProduct(
      pathname: string,
      name: string,
      metaTitle: string,
      metaDescription: string,
      images: IImage[],
      description: IDescription,
      price: IPrice,
      specification: ISpecification,
      mainCategory: string
    ) {
      const { dedupedPrimary, dedupedSecondary } = deduplicateImages(images, description.images);
      const newProduct: IProduct = {
        pathname,
        name,
        metaTitle,
        metaDescription,
        images: dedupedPrimary,
        description: {
          ...description,
          images: dedupedSecondary,
        },
        price,
        specification,
        mainCategory,
      };
      this.products.push(newProduct);
    },
    clearProductStore() {
      this.products = new Array<IProduct>();
    },
    hasUrl(pathname: string) {
      return this.products.some((product) => product.pathname === pathname);
    },
    getProductByPathname(pathname: string) {
      return this.products.find((product) => product.pathname === pathname);
    },
  },
});
